import type { Ref as ReactRef, InputHTMLAttributes, ChangeEvent } from "react";
import { useState, useEffect, forwardRef, useRef } from "react";
import { useDebounce } from "use-debounce";
import styles from "./TextInputDelay.module.css";

type T = HTMLInputElement;

type Props = InputHTMLAttributes<T> & {
  value: string;
  delayInMs?: number;
  onValueChange: (value: string) => void;
};

type Ref = ReactRef<T>;

type State = string;

function TextInputDelay(
  { delayInMs = 400, onValueChange, ...props }: Props,
  ref: Ref
) {
  const [text, setText] = useState<State>(
    typeof props.value === "string" ? props.value : ""
  );
  const prevValueRef = useRef(props.value);

  const [value] = useDebounce(text, delayInMs);

  function handleChange(e: ChangeEvent<T>) {
    setText(e.target.value);
  }

  useEffect(() => {
    if (props.value !== prevValueRef.current) {
      setText(props.value);
      prevValueRef.current = props.value;
    }
  }, [props.value]);

  useEffect(
    () => {
      onValueChange(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );

  return (
    <input
      {...props}
      ref={ref}
      value={text}
      onChange={handleChange}
      className={styles.TextInputDelay}
    />
  );
}

export default forwardRef(TextInputDelay);
